import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useEditorState } from '@tiptap/react';
import { useLanguage } from '../../i18n/LanguageContext';
import './DocumentEditor.css';

// Import hooks
import { useDocumentEditor, useDocumentData, usePageCalculation } from './hooks';
import { renameDocument, importLocalFiles, restoreDocumentVersion } from '../../api/documentApi';
import { canEdit, isOwner } from '../../api/sharingApi';
import SharedBanner from '../sharing/SharedBanner';
import ConflictDialog from '../sharing/ConflictDialog';
import { useShare } from '../sharing/useShare';

// Import components
import {
    DocumentToolbar,
    MenuBar,
    FormattingToolbar,
    HorizontalRuler,
    DocumentPages,
} from './components';

// Import modals
import {
    WordCountModal,
    LiveWordCount,
    ProofreadModal,
    CustomSpacingModal,
    ImportLocalFilesModal,
    ImportGoogleDocsModal,
    VersionHistoryModal,
} from './modals';
import type { ProofreadSettings } from './modals';

// Import utilities
import {
    normalizeHTMLContent,
    calculateWordCount,
    handleImagePaste,
    insertImageFromUrl,
    exportDocument,
} from './utils/documentHelpers';
import type { ExportFormat } from './utils/documentHelpers';

import * as toolbarHandlers from './utils/toolbarHandlers';

const DEFAULT_LINE_SPACING = 1.0;
const DEFAULT_SPACING_BEFORE = 0;
const DEFAULT_SPACING_AFTER = 0;

interface DocumentEditorProps {
    documentId: number;
    onClose: (e?: React.MouseEvent) => void;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({ documentId, onClose }) => {
    const { t } = useLanguage();

    // UI State
    const [showMenuDropdown, setShowMenuDropdown] = useState<string | null>(null);
    const [showDocInfo, setShowDocInfo] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState<'text' | 'highlight' | 'outline' | null>(null);
    const [currentTextColor, setCurrentTextColor] = useState('#ffffff');
    const [currentHighlightColor, setCurrentHighlightColor] = useState('#ffff00');
    const [fontSize, setFontSize] = useState(14);
    const [showWordCountModal, setShowWordCountModal] = useState(false);
    const [displayWordCountWhileTyping, setDisplayWordCountWhileTyping] = useState(false);
    const [showProofreadModal, setShowProofreadModal] = useState(false);
    const [showCustomSpacingModal, setShowCustomSpacingModal] = useState(false);
    const [showImportLocalFilesModal, setShowImportLocalFilesModal] = useState(false);
    const [showImportGoogleDocsModal, setShowImportGoogleDocsModal] = useState(false);
    const [showVersionHistory, setShowVersionHistory] = useState(false);

    // Title editing
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState('');
    const titleInputRef = useRef<HTMLInputElement>(null);

    // View settings
    const [printLayout, setPrintLayout] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const editorRootRef = useRef<HTMLDivElement>(null);

    // Ruler and spacing
    const [showRuler, setShowRuler] = useState(true);
    const [leftMargin, setLeftMargin] = useState(100);
    const [rightMargin, setRightMargin] = useState(918);
    const [isDraggingMargin, setIsDraggingMargin] = useState<'left' | 'right' | null>(null);
    const [dragGuidePosition, setDragGuidePosition] = useState<number>(0);
    const rulerRef = useRef<HTMLDivElement>(null);

    // Document-wide spacing defaults. Per-block overrides live on the nodes
    // themselves (see the ParagraphSpacing extension), so these only supply
    // the baseline for text the user hasn't styled.
    const lineSpacing = DEFAULT_LINE_SPACING;
    const paragraphSpacingBefore = DEFAULT_SPACING_BEFORE;
    const paragraphSpacingAfter = DEFAULT_SPACING_AFTER;
    const [keepWithNext, setKeepWithNext] = useState(false);
    const [keepLinesTogether, setKeepLinesTogether] = useState(false);
    const [preventSingleLines, setPreventSingleLines] = useState(false);

    // Proofreading
    const [proofreadSettings, setProofreadSettings] = useState<ProofreadSettings>({
        spelling: true,
        grammar: true,
        activeVoice: true,
        conciseness: true,
        sentenceSplit: true,
    });
    const [customDictionary, setCustomDictionary] = useState<string[]>(() => {
        const saved = localStorage.getItem('customDictionary');
        return saved ? JSON.parse(saved) : [];
    });
    const addToDictionary = useCallback((word: string) => {
        setCustomDictionary((words) => (words.includes(word) ? words : [...words, word]));
    }, []);

    // Use custom hooks
    const {
        documentData,
        setDocumentData,
        isLoading,
        isSaving,
        lastSaved,
        error,
        contentLoaded,
        conflict,
        acceptTheirVersion,
        keepMyVersion,
        setSaveBase,
        saveNow,
        cancelPendingSave,
        scheduleAutoSave,
        lastContentRef,
        initialContentRef,
    } = useDocumentData({ documentId });

    // A document shared with this user: a viewer reads it, an editor changes
    // the words but not its name, folder or who else can see it.
    const access = documentData?.access;
    const readOnly = !canEdit(access);
    const owned = isOwner(access);
    const { openShare, shareUi } = useShare('document', documentId, documentData?.name || '');

    const {
        editor: documentEditor,
        editorKey,
        pageCount,
    } = useDocumentEditor({
        proofreadSettings,
        customDictionary,
        onAddToDictionary: addToDictionary,
        printLayout,
        onUpdate: (content) => {
            if (content !== lastContentRef.current) {
                lastContentRef.current = content;
                if (!readOnly) scheduleAutoSave(content);
            }
        },
    });

    // The helper modules are plain functions and cannot reach the language
    // hook, so the strings they show go down with the call.
    const toolbarMessages = useMemo(() => ({
        clipboardBlockedCut: t.editor.clipboardBlockedCut,
        clipboardBlockedCopy: t.editor.clipboardBlockedCopy,
        clipboardBlockedPaste: t.editor.clipboardBlockedPaste,
        linkUrlPrompt: t.editor.linkUrlPrompt,
    }), [t]);

    const helperMessages = useMemo(() => ({
        imagePasteFailed: t.editor.imagePasteFailed,
        imageUploadFailed: t.editor.imageUploadFailed,
        exportFailed: t.editor.exportFailed,
    }), [t]);

    const { editorContainerRef } = usePageCalculation({
        editor: documentEditor,
        contentLoaded,
    });

    // Spacing of the block under the cursor. Null means "never set", so the
    // menus and the Custom spacing dialog fall back to the document default.
    const blockSpacing = useEditorState({
        editor: documentEditor,
        selector: ({ editor }) => {
            if (!editor) return null;
            const attrs = ['paragraph', 'heading', 'bulletList', 'orderedList']
                .map((type) => editor.getAttributes(type))
                .find((candidate) => candidate && Object.keys(candidate).length > 0) || {};
            return {
                lineHeight: (attrs.lineHeight as number | null) ?? null,
                spaceBefore: (attrs.spaceBefore as number | null) ?? null,
                spaceAfter: (attrs.spaceAfter as number | null) ?? null,
                pageBreakBefore: Boolean(attrs.pageBreakBefore),
            };
        },
    });

    const activeLineSpacing = blockSpacing?.lineHeight ?? lineSpacing;
    const activeSpacingBefore = blockSpacing?.spaceBefore ?? paragraphSpacingBefore;
    const activeSpacingAfter = blockSpacing?.spaceAfter ?? paragraphSpacingAfter;

    // Set content once per loaded document. Re-running setContent on every
    // documentData identity change (e.g. after a title save) would revert
    // unsaved edits and wipe the undo/redo history.
    const loadedDocIdRef = useRef<number | null>(null);
    useEffect(() => {
        if (documentData && documentData.content && documentEditor
            && loadedDocIdRef.current !== documentData.id) {
            loadedDocIdRef.current = documentData.id;
            const normalized = normalizeHTMLContent(documentData.content);
            documentEditor.commands.setContent(normalized);
            lastContentRef.current = normalized;
            initialContentRef.current = normalized;
        }
    }, [documentData, documentEditor, lastContentRef, initialContentRef]);

    // A viewer's editor is a reader: ProseMirror itself refuses the keystrokes,
    // rather than the writing being accepted and then rejected by the server.
    useEffect(() => {
        documentEditor?.setEditable(!readOnly);
    }, [documentEditor, readOnly, documentData]);

    // Ctrl/Cmd+S is muscle memory for anyone who has ever used a word
    // processor. Left alone the browser answers with "save this page as
    // HTML", which is never what the writer meant — take the key over and
    // flush the pending autosave instead.
    useEffect(() => {
        const handleSaveShortcut = (e: KeyboardEvent) => {
            if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
            if (e.key.toLowerCase() !== 's') return;
            e.preventDefault();
            if (!readOnly) saveNow(lastContentRef.current);
        };

        document.addEventListener('keydown', handleSaveShortcut);
        return () => document.removeEventListener('keydown', handleSaveShortcut);
    }, [saveNow, lastContentRef, readOnly]);

    // Track fullscreen state (covers Esc exiting fullscreen)
    useEffect(() => {
        const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.menu-item') && !target.closest('.doc-info-container')) {
                setShowMenuDropdown(null);
                setShowDocInfo(false);
            }
        };
        
        if (showMenuDropdown || showDocInfo) {
            document.addEventListener('click', handleClickOutside);
        }
        
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showMenuDropdown, showDocInfo]);

    // Handle image paste
    useEffect(() => {
        if (!documentEditor || readOnly) return;
        
        const handlePaste = (event: ClipboardEvent) => {
            handleImagePaste(event, documentEditor, documentId, helperMessages);
        };
        
        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [documentEditor, documentId, readOnly, helperMessages]);

    // Handle escape key to close modals
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (showWordCountModal) setShowWordCountModal(false);
                if (showProofreadModal) setShowProofreadModal(false);
                if (showCustomSpacingModal) setShowCustomSpacingModal(false);
            }
        };
        
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showWordCountModal, showProofreadModal, showCustomSpacingModal]);

    // Handle margin slider dragging
    useEffect(() => {
        if (!isDraggingMargin) return;
        
        const handleMouseMove = (e: MouseEvent) => {
            if (!rulerRef.current) return;
            
            const rulerRect = rulerRef.current.getBoundingClientRect();
            const relativeX = e.clientX - rulerRect.left;
            const clampedX = Math.max(0, Math.min(relativeX, 1018));
            
            setDragGuidePosition(clampedX);
            if (isDraggingMargin === 'left') {
                setLeftMargin(clampedX);
            } else {
                setRightMargin(clampedX);
            }
        };
        
        const handleMouseUp = () => {
            setIsDraggingMargin(null);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingMargin, leftMargin, rightMargin]);

    // Save custom dictionary to localStorage
    useEffect(() => {
        localStorage.setItem('customDictionary', JSON.stringify(customDictionary));
    }, [customDictionary]);

    // Focus title input when editing starts
    useEffect(() => {
        if (isEditingTitle && titleInputRef.current) {
            titleInputRef.current.focus();
            titleInputRef.current.select();
        }
    }, [isEditingTitle]);

    // Title handlers
    const handleTitleClick = () => {
        if (documentData) {
            setEditedTitle(documentData.name);
            setIsEditingTitle(true);
        }
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditedTitle(e.target.value);
    };

    const handleTitleBlur = async () => {
        await saveTitle();
    };

    const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            titleInputRef.current?.blur();
        } else if (e.key === 'Escape') {
            setIsEditingTitle(false);
            setEditedTitle('');
        }
    };

    const saveTitle = async () => {
        if (!documentData || !editedTitle.trim() || editedTitle.trim() === documentData.name) {
            setIsEditingTitle(false);
            return;
        }

        const name = editedTitle.trim();
        const previous = documentData.name;

        // Optimistic, then persisted. This used to stop at the first line,
        // so the new name lived only in this component and was gone the
        // moment the document list reloaded.
        setDocumentData({ ...documentData, name });
        setIsEditingTitle(false);

        try {
            const renamed = await renameDocument(documentData.id, name);
            // A rename is a save like any other: it moves the document's
            // updated_at. Without taking the new one, the next autosave
            // sends a base from before the rename and is told it is stale
            // -- by the writer's own edit, with their unsaved text on
            // screen and a dialog offering to replace it.
            if (renamed?.updated_at) setSaveBase(renamed.updated_at);
        } catch (error) {
            console.error('Failed to save title:', error);
            setDocumentData({ ...documentData, name: previous });
            setEditedTitle(previous);
        }
    };

    const toggleMenu = (menu: string) => {
        setShowMenuDropdown(showMenuDropdown === menu ? null : menu);
    };

    const toggleFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            editorRootRef.current?.requestFullscreen?.();
        }
    };


    // Actions that need component state or the document id are handled here;
    // everything else goes to the shared editor-command handler.
    const handleMenuAction = (action: string) => {
        switch (action) {
            case 'printLayout':
                setPrintLayout((current) => !current);
                setShowMenuDropdown(null);
                return;
            case 'fullScreen':
                toggleFullscreen();
                setShowMenuDropdown(null);
                return;
            case 'versionHistory':
                setShowVersionHistory(true);
                setShowMenuDropdown(null);
                return;
            case 'insertImage':
                insertImageFromUrl(documentEditor, documentId, helperMessages);
                setShowMenuDropdown(null);
                return;
            case 'downloadPdf':
            case 'downloadDocx':
            case 'downloadRtf':
            case 'downloadHtml': {
                const format = action.replace('download', '').toLowerCase() as ExportFormat;
                exportDocument(documentId, format, documentData?.name, helperMessages);
                setShowMenuDropdown(null);
                return;
            }
            default:
                toolbarHandlers.handleMenuAction(
                    action,
                    documentEditor,
                    setShowMenuDropdown,
                    setShowWordCountModal,
                    setShowProofreadModal,
                    setShowImportLocalFilesModal,
                    setShowImportGoogleDocsModal,
                    toolbarMessages
                );
        }
    };

    // Unsaved typing is saved first, so the server keeps it as a version
    // before the restore replaces it. The restored content then becomes the
    // autosave baseline, and nothing still queued from before gets to write
    // the old text back over it.
    const handleRestoreVersion = async (versionId: number) => {
        await saveNow(lastContentRef.current);
        const restored = await restoreDocumentVersion(documentId, versionId);
        cancelPendingSave();
        if (documentEditor) {
            documentEditor.commands.setContent(
                normalizeHTMLContent(restored.content), { emitUpdate: false });
            lastContentRef.current = documentEditor.getHTML();
            initialContentRef.current = lastContentRef.current;
        }
        setDocumentData(restored);
        setSaveBase(restored.updated_at);
    };

    // Imported files become documents of their own, so there is nothing to
    // show inside this one — the editor closes back to the list where they
    // now are. Previously this waited two seconds and reported success
    // without importing anything at all.
    const handleImportLocalFiles = async (files: File[]) => {
        const imported = await importLocalFiles(files);
        setShowImportLocalFilesModal(false);
        if (imported.length) onClose({} as React.MouseEvent);
    };


    // Their version replaces what is on screen; mine is saved over theirs.
    // Either way the choice is the writer's, and nothing happens until they
    // make it — the editor keeps their text while the dialog is up.
    const handleLoadTheirVersion = () => {
        if (!conflict || !documentEditor) return;
        const normalized = normalizeHTMLContent(conflict.content);
        documentEditor.commands.setContent(normalized, { emitUpdate: false });
        lastContentRef.current = documentEditor.getHTML();
        initialContentRef.current = lastContentRef.current;
        acceptTheirVersion();
    };

    const handleKeepMyVersion = () => {
        keepMyVersion(lastContentRef.current);
    };

    if (isLoading) {
        return (
            <div className="document-editor-loading">
                <div className="loading-spinner"></div>
                <p>{t.editor.loadingDocument}</p>
            </div>
        );
    }

    if (error || !documentData) {
        return (
            <div className="document-editor-error">
                <p>{error || t.editor.loadFailed}</p>
                <button 
                    type="button"
                    onClick={(e) => onClose(e)}
                    className="doc-editor-back-btn">
                    ← {t.editor.backToEntryForm}
                </button>
            </div>
        );
    }

    return (
        <div className={`document-editor${printLayout ? '' : ' pageless'}`} ref={editorRootRef}>
            <DocumentToolbar
                documentData={documentData}
                isEditingTitle={isEditingTitle}
                editedTitle={editedTitle}
                titleInputRef={titleInputRef}
                isSaving={isSaving}
                lastSaved={lastSaved}
                showDocInfo={showDocInfo}
                t={t}
                onClose={onClose}
                onTitleClick={handleTitleClick}
                onTitleChange={handleTitleChange}
                onTitleBlur={handleTitleBlur}
                onTitleKeyDown={handleTitleKeyDown}
                onToggleDocInfo={() => setShowDocInfo(!showDocInfo)}
                onShowVersionHistory={() => setShowVersionHistory(true)}
                canRename={owned}
                onShare={owned ? openShare : undefined}
                shareLabel={t.sharing.share}
            />

            <SharedBanner access={access} />

            <MenuBar
                canImport={owned}
                readOnly={readOnly}
                t={t}
                showMenuDropdown={showMenuDropdown}
                showRuler={showRuler}
                printLayout={printLayout}
                isFullscreen={isFullscreen}
                lineSpacing={activeLineSpacing}
                keepWithNext={keepWithNext}
                keepLinesTogether={keepLinesTogether}
                preventSingleLines={preventSingleLines}
                pageBreakBefore={blockSpacing?.pageBreakBefore ?? false}
                onToggleMenu={toggleMenu}
                onMenuAction={handleMenuAction}
                onSetShowRuler={setShowRuler}
                onSetShowMenuDropdown={setShowMenuDropdown}
                onSetShowCustomSpacingModal={setShowCustomSpacingModal}
                onHandleSpacingChange={(spacing) =>
                    toolbarHandlers.handleSpacingChange(documentEditor, spacing, setShowMenuDropdown)
                }
                onHandleAddSpaceBefore={() =>
                    toolbarHandlers.handleAddSpaceBefore(documentEditor, setShowMenuDropdown)
                }
                onHandleRemoveSpaceBefore={() =>
                    toolbarHandlers.handleRemoveSpaceBefore(documentEditor, setShowMenuDropdown)
                }
                onHandleAddPageBreak={() =>
                    toolbarHandlers.handleAddPageBreak(documentEditor, setShowMenuDropdown)
                }
                onSetKeepWithNext={setKeepWithNext}
                onSetKeepLinesTogether={setKeepLinesTogether}
                onSetPreventSingleLines={setPreventSingleLines}
                onClearFormatting={() => toolbarHandlers.clearFormatting(documentEditor)}
            />

            {!readOnly && <FormattingToolbar
                documentEditor={documentEditor}
                t={t}
                fontSize={fontSize}
                showColorPicker={showColorPicker}
                currentTextColor={currentTextColor}
                currentHighlightColor={currentHighlightColor}
                onSetTextStyle={(level) => toolbarHandlers.setTextStyle(documentEditor, level)}
                onSetFontFamily={(font) => toolbarHandlers.setFontFamily(documentEditor, font)}
                onHandleFontSizeChange={(size) =>
                    toolbarHandlers.handleFontSizeChange(documentEditor, size, setFontSize)
                }
                onIncrementFontSize={(currentSize) =>
                    toolbarHandlers.incrementFontSize(currentSize, documentEditor, setFontSize)
                }
                onDecrementFontSize={(currentSize) =>
                    toolbarHandlers.decrementFontSize(currentSize, documentEditor, setFontSize)
                }
                onSetShowColorPicker={setShowColorPicker}
                onSetTextColor={(color) =>
                    toolbarHandlers.setTextColor(documentEditor, color, setCurrentTextColor, setShowColorPicker)
                }
                onSetHighlightColor={(color) =>
                    toolbarHandlers.setHighlightColor(documentEditor, color, setCurrentHighlightColor, setShowColorPicker, setCurrentTextColor)
                }
                onSetAlignment={(align) => toolbarHandlers.setAlignment(documentEditor, align)}
                onApplyOutline={(width, color) => toolbarHandlers.applyTextOutline(documentEditor, width, color)}
                onRemoveOutline={() => toolbarHandlers.removeTextOutline(documentEditor)}
                onInsertImageFromUrl={() => insertImageFromUrl(documentEditor, documentId, helperMessages)}
                onClearFormatting={() => toolbarHandlers.clearFormatting(documentEditor)}
            />}

            <HorizontalRuler
                showRuler={showRuler}
                rulerRef={rulerRef}
                leftMargin={leftMargin}
                rightMargin={rightMargin}
                isDraggingMargin={isDraggingMargin}
                dragGuidePosition={dragGuidePosition}
                onSetIsDraggingMargin={setIsDraggingMargin}
                onSetDragGuidePosition={setDragGuidePosition}
            />

            <DocumentPages
                t={t}
                showRuler={showRuler}
                documentEditor={documentEditor}
                editorKey={editorKey}
                pageCount={pageCount}
                editorContainerRef={editorContainerRef}
                leftMargin={leftMargin}
                rightMargin={rightMargin}
                lineSpacing={lineSpacing}
                paragraphSpacingBefore={paragraphSpacingBefore}
                paragraphSpacingAfter={paragraphSpacingAfter}
                keepLinesTogether={keepLinesTogether}
                keepWithNext={keepWithNext}
                preventSingleLines={preventSingleLines}
            />

            <WordCountModal
                t={t}
                show={showWordCountModal}
                stats={calculateWordCount(documentEditor)}
                displayWhileTyping={displayWordCountWhileTyping}
                onClose={() => setShowWordCountModal(false)}
                onToggleDisplay={setDisplayWordCountWhileTyping}
            />

            <LiveWordCount
                t={t}
                show={displayWordCountWhileTyping}
                wordCount={calculateWordCount(documentEditor).words}
            />

            <ProofreadModal
                t={t}
                show={showProofreadModal}
                settings={proofreadSettings}
                onClose={() => setShowProofreadModal(false)}
                onUpdateSettings={setProofreadSettings}
            />

            <CustomSpacingModal
                t={t}
                show={showCustomSpacingModal}
                initialLineSpacing={activeLineSpacing}
                initialSpacingBefore={activeSpacingBefore}
                initialSpacingAfter={activeSpacingAfter}
                onClose={() => setShowCustomSpacingModal(false)}
                onApply={(newLineSpacing, newSpacingBefore, newSpacingAfter) => {
                    // Applies to the selected blocks, not the whole document
                    toolbarHandlers.applyCustomSpacing(
                        documentEditor, newLineSpacing, newSpacingBefore, newSpacingAfter
                    );
                }}
            />

            {showImportLocalFilesModal && (
                <ImportLocalFilesModal
                    t={t}
                    onClose={() => setShowImportLocalFilesModal(false)}
                    onImport={handleImportLocalFiles}
                />
            )}

            {showVersionHistory && (
                <VersionHistoryModal
                    documentId={documentId}
                    t={t}
                    canRestore={!readOnly}
                    onClose={() => setShowVersionHistory(false)}
                    onRestore={handleRestoreVersion}
                />
            )}

            {conflict && (
                <ConflictDialog
                    lastEditedBy={conflict.last_edited_by}
                    onLoadTheirs={handleLoadTheirVersion}
                    onKeepMine={handleKeepMyVersion}
                />
            )}

            {shareUi}

            {showImportGoogleDocsModal && (
                <ImportGoogleDocsModal
                    t={t}
                    onClose={() => setShowImportGoogleDocsModal(false)}
                    onImported={() => setShowImportGoogleDocsModal(false)}
                />
            )}
        </div>
    );
};

export default DocumentEditor;
